import {
  IPartFinderFamily,
  IPartFinderFamilyComparison,
  IPartFinderFamilyPart,
  IPartFinderFamilyPayload,
  IPartFinderFamilyVersion,
} from 'companySettings/types';
import { mapRecordsFromServer } from 'core/mappers';
import { partfinitiClient } from 'core/services';
import { GetManyResponse, IRequestPayload } from 'core/types';
import { getQueryStringToSnakeCase, objectKeysSnakeCaseToCamelCase } from 'core/utils';

export const getPartFinderFamilies = async (
  requestPayload?: IRequestPayload,
  signal?: AbortSignal
): Promise<GetManyResponse<IPartFinderFamily>> => {
  const { data } = await partfinitiClient.get(
    `/part_v2/finder-family-list/list?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return mapRecordsFromServer<IPartFinderFamily>(data?.data, data?.recordsFiltered);
};

export const savePartFinderFamily = async (
  partFinderFamily: IPartFinderFamilyPayload
): Promise<{ success: boolean; partFinderFamily: IPartFinderFamily }> =>
  partfinitiClient.postForm('/part_v2/finder-family-edit/save', { partFinderFamily });

// The parts of a family's working version, in their order.
export const getPartFinderFamilyParts = async (
  familyId: number,
  signal?: AbortSignal
): Promise<GetManyResponse<IPartFinderFamilyPart>> => {
  const { data } = await partfinitiClient.get(
    `/part_v2/finder-family-part-list/list?${getQueryStringToSnakeCase({
      page: 1,
      pageSize: 5000,
      filters: { familyId },
    })}`,
    signal
  );

  return mapRecordsFromServer<IPartFinderFamilyPart>(data?.data, data?.recordsFiltered);
};

// A picked part as a family member would show it; nothing is saved.
export const resolvePartFinderFamilyPart = async ({
  number,
  manufacturerId,
}: {
  number: string;
  manufacturerId: number;
}): Promise<{ success: boolean; part: IPartFinderFamilyPart }> => {
  const { data } = await partfinitiClient.get(
    `/part_v2/finder-family-part-list/resolve?${getQueryStringToSnakeCase({
      number,
      manufacturerId,
    })}`
  );

  return { success: Boolean(data?.success), part: objectKeysSnakeCaseToCamelCase(data?.part) };
};

// One family of the company as the list shows it, or undefined when it is not one.
export const getPartFinderFamily = async (
  id: number,
  signal?: AbortSignal
): Promise<IPartFinderFamily | undefined> => {
  const { records } = await getPartFinderFamilies(
    { filters: { id }, page: 1, pageSize: 1 },
    signal
  );

  return records?.[0];
};

// Publishes the drafts of the families; one with no draft is left as it is.
export const publishPartFinderFamilies = async (
  ids: number[]
): Promise<{ success: boolean; published: number }> =>
  partfinitiClient.postForm('/part_v2/finder-family-edit/publish', { ids });

// Takes the families off the finder; one that is not on it is left as it is.
export const unpublishPartFinderFamilies = async (
  ids: number[]
): Promise<{ success: boolean; unpublished: number }> =>
  partfinitiClient.postForm('/part_v2/finder-family-edit/unpublish', { ids });

// Removes the families: they leave the list and the finder, their versions stay.
export const removePartFinderFamilies = async (ids: number[]): Promise<{ success: boolean }> =>
  partfinitiClient.postForm('/part_v2/finder-family-edit/remove', { ids });

// The family's draft, if it has one, then its published versions, newest first.
export const getPartFinderFamilyVersions = async (
  familyId: number,
  signal?: AbortSignal
): Promise<IPartFinderFamilyVersion[]> => {
  const { data } = await partfinitiClient.get(
    `/part_v2/finder-family-version-list/list?${getQueryStringToSnakeCase({ familyId })}`,
    signal
  );

  return objectKeysSnakeCaseToCamelCase(data?.versions ?? []);
};

export const comparePartFinderFamilyVersions = async (
  { fromVersionId, toVersionId }: { fromVersionId: number; toVersionId: number },
  signal?: AbortSignal
): Promise<IPartFinderFamilyComparison> => {
  const { data } = await partfinitiClient.get(
    `/part_v2/finder-family-version-list/compare?${getQueryStringToSnakeCase({
      from: fromVersionId,
      to: toVersionId,
    })}`,
    signal
  );

  return objectKeysSnakeCaseToCamelCase(data?.comparison);
};
